<template>
  <nav
    ref="root"
    data-journey-strip
    :aria-label="i18n(translations.stripLabel)"
    class="raw-glass rounded-full pointer-events-auto p-4"
  >
    <ol
      ref="track"
      data-journey-track
      :data-dragging="dragging || undefined"
      :style="{ '--fade-start': fadeStart, '--fade-end': fadeEnd }"
      class="relative flex select-none gap-4 overflow-x-auto overscroll-x-contain [mask-image:linear-gradient(to_right,transparent_0,black_var(--fade-start),black_calc(100%_-_var(--fade-end)),transparent_100%)]"
    >
      <li v-for="item in stops" :key="item.slug">
        <button
          type="button"
          data-journey-tab
          :data-active="item.step === current || undefined"
          :aria-current="item.step === current ? 'step' : undefined"
          class="raw-cta-3 rounded-full h-32 whitespace-nowrap px-12"
          @click="select(item.step)"
        >
          {{ i18n(item.label) }}
        </button>
      </li>
    </ol>
  </nav>
</template>

<script lang="ts">
export interface JourneyStripStop {
  /** The 1-based index `goToStop` takes, not the tab's position in this list. */
  step: number
  slug: string
  label: I18nDescriptor
}
</script>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useEventListener, useResizeObserver } from '@vueuse/core'

import { useDraggableRail } from '~/scopes/campaign/composables/useDraggableRail'
import { useElementHeightVar } from '~/scopes/campaign/composables/useElementHeightVar'
import type { I18nDescriptor } from '~/scopes/campaign/data/metals/types'
import { prefersReducedMotion } from '~/scopes/campaign/utils/prefersReducedMotion'

import translations from './JourneyOverlay.translations'

const props = defineProps<{
  stops: readonly JourneyStripStop[]
  /** The stop on screen. Every labelled stop is listed, so one of them is always it. */
  current: number
}>()

const emit = defineEmits<{ select: [step: number] }>()
const i18n = useI18n()

const root = ref<HTMLElement | null>(null)
const track = ref<HTMLElement | null>(null)
const { dragging } = useDraggableRail(track)

// The camera's twin of the card reserves this row, so the height has to reach CSS.
useElementHeightVar(root, '--raw-journey-nav-height')

const FADE = '8px'
const fadeStart = ref('0px')
const fadeEnd = ref('0px')

// Fade only the edge that still hides something, as `UI/LenisScroll.vue` does vertically.
function updateFades() {
  const el = track.value
  if (!el) return
  const max = el.scrollWidth - el.clientWidth
  fadeStart.value = el.scrollLeft > 1 ? FADE : '0px'
  fadeEnd.value = el.scrollLeft < max - 1 ? FADE : '0px'
}

/** Centre the current tab; `scrollTo` clamps itself, so no tab is out of reach. */
function centreActive(smooth = true) {
  const el = track.value
  const tab = el?.querySelector<HTMLElement>('[data-journey-tab][data-active]')
  if (!el || !tab) return
  el.scrollTo({
    left: tab.offsetLeft - (el.clientWidth - tab.offsetWidth) / 2,
    behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'auto',
  })
}

// The first measurable width is the one to centre on: before it the track is zero-wide and
// every scroll is a no-op. (Superseded: this also covered the bookends, which used to drop
// the strip to `display: none` and bring it back needing a re-centre. It stays in the
// layout now, so the only zero-width pass is the one before the first paint.)
let hadWidth = false

function onResize() {
  const width = track.value?.clientWidth ?? 0
  if (width > 0 && !hadWidth) centreActive(false)
  hadWidth = width > 0
  updateFades()
}

function focusActive() {
  root.value
    ?.querySelector<HTMLElement>('[data-journey-tab][data-active]')
    ?.focus()
}

function holdsFocus() {
  return Boolean(root.value?.contains(document.activeElement))
}

function select(step: number) {
  if (step !== props.current) emit('select', step)
}

useEventListener(track, 'scroll', updateFades)
useResizeObserver(track, onResize)
watch(
  () => props.current,
  () => centreActive(),
  { flush: 'post' },
)
// The first centring rides the ResizeObserver's own opening callback; this second pass
// is for the real font face, a few px wider than the fallback the first one measured.
onMounted(async () => {
  await document.fonts?.ready
  updateFades()
  centreActive(false)
})

defineExpose({ focusActive, holdsFocus })
</script>
